// Firebase service for user search functionality
import { 
  collection, 
  doc,
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  getDocs,
  startAt,
  endAt
} from "firebase/firestore";
import { db } from "./firebase-config";

// Enhanced cache for search results with LRU eviction
const searchCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 100;

// Performance monitoring
let searchMetrics = {
  totalSearches: 0,
  cacheHits: 0,
  averageResponseTime: 0,
  errorCount: 0
};

/**
 * Search for users by name or email
 * @param {string} searchTerm - Search term to match against
 * @param {number} maxResults - Maximum number of results to return
 * @returns {Promise<Object>} - Search results with success status
 */
export const searchUsers = async (searchTerm, maxResults = 10) => {
  const startTime = performance.now();
  searchMetrics.totalSearches++;
  
  try {
    console.log('Searching users with term:', searchTerm);
    
    // Handle empty search term - return recent active users
    if (!searchTerm || searchTerm.trim().length < 1) {
      try {
        const recentUsersQuery = query(
          collection(db, 'user_profiles'),
          where('onboardingComplete', '==', true),
          where('isHidden', '!=', true),
          orderBy('lastLogin', 'desc'),
          limit(maxResults)
        );
        
        const recentSnapshot = await getDocs(recentUsersQuery);
        const recentUsers = [];
        
        recentSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          if (userData.displayName && !userData.isHidden) {
            recentUsers.push({
              id: doc.id,
              displayName: userData.displayName,
              firstName: userData.basicInfo?.firstName || '',
              lastName: userData.basicInfo?.lastName || '',
              currentRole: userData.basicInfo?.currentRole || '',
              companyName: userData.basicInfo?.companyName || '',
              profilePicture: userData.profilePicture || userData.basicInfo?.photoURL || userData.photoURL || '',
              lastActive: userData.lastLogin || userData.lastUpdated || userData.createdAt,
              searchScore: 10 // Base score for recent users
            });
          }
        });
        
        return {
          success: true,
          data: recentUsers
        };
      } catch (error) {
        console.log('Failed to fetch recent users:', error);
        return {
          success: true,
          data: []
        };
      }
    }
    
    const normalizedTerm = searchTerm.trim().toLowerCase();
    
    // Check cache first
    const cacheKey = `${normalizedTerm}_${maxResults}`;
    const cachedResult = getCachedResult(cacheKey);
    if (cachedResult) {
      searchMetrics.cacheHits++;
      console.log('Returning cached search results');
      return cachedResult;
    }
    
    // Build search queries - Firestore requires orderBy field to come first in compound queries
    // So we'll use a simpler approach: get users with basic filters, then filter client-side
    const searchQueries = [];
    const capitalizedTerm = normalizedTerm.charAt(0).toUpperCase() + normalizedTerm.slice(1);
    
    console.log(`Search variants: lowercase="${normalizedTerm}", capitalized="${capitalizedTerm}"`);
    
    // Strategy 1: Simple range queries without isHidden filter (we'll filter that client-side)
    // This avoids Firestore composite index requirements
    
    // Search by display name (try both lowercase and capitalized)
    for (const searchVariant of [normalizedTerm, capitalizedTerm]) {
      try {
        const displayNameQuery = query(
          collection(db, 'user_profiles'),
          where('onboardingComplete', '==', true),
          orderBy('displayName'),
          startAt(searchVariant),
          endAt(searchVariant + '\uf8ff'),
          limit(maxResults * 2) // Get more to account for filtering
        );
        searchQueries.push(displayNameQuery);
        console.log(`Added displayName query for: ${searchVariant}`);
      } catch (error) {
        console.log(`Display name query failed for ${searchVariant}:`, error);
      }
    }
    
    // Search by first name (try both lowercase and capitalized)
    for (const searchVariant of [normalizedTerm, capitalizedTerm]) {
      try {
        const firstNameQuery = query(
          collection(db, 'user_profiles'),
          where('onboardingComplete', '==', true),
          where('basicInfo.firstName', '>=', searchVariant),
          where('basicInfo.firstName', '<=', searchVariant + '\uf8ff'),
          limit(maxResults * 2) // Get more to account for filtering
        );
        searchQueries.push(firstNameQuery);
        console.log(`Added firstName query for: ${searchVariant}`);
      } catch (error) {
        console.log(`First name query failed for ${searchVariant}:`, error);
      }
    }
    
    // Search by last name (try both lowercase and capitalized)
    for (const searchVariant of [normalizedTerm, capitalizedTerm]) {
      try {
        const lastNameQuery = query(
          collection(db, 'user_profiles'),
          where('onboardingComplete', '==', true),
          where('basicInfo.lastName', '>=', searchVariant),
          where('basicInfo.lastName', '<=', searchVariant + '\uf8ff'),
          limit(maxResults * 2) // Get more to account for filtering
        );
        searchQueries.push(lastNameQuery);
        console.log(`Added lastName query for: ${searchVariant}`);
      } catch (error) {
        console.log(`Last name query failed for ${searchVariant}:`, error);
      }
    }
    
    // Execute all queries in parallel
    const queryPromises = searchQueries.map(q => getDocs(q));
    const queryResults = await Promise.all(queryPromises);
    
    // Combine and deduplicate results
    const userMap = new Map();
    
    queryResults.forEach((snapshot, queryIndex) => {
      console.log(`Processing query ${queryIndex} with ${snapshot.docs.length} results`);
      
      snapshot.docs.forEach(doc => {
        const userData = doc.data();
        const userId = doc.id;
        
        console.log(`Processing user ${userId}:`, {
          displayName: userData.displayName,
          firstName: userData.basicInfo?.firstName,
          lastName: userData.basicInfo?.lastName,
          isHidden: userData.isHidden,
          onboardingComplete: userData.onboardingComplete
        });
        
        // Skip if already added, if user data is incomplete, or if user is hidden
        if (userMap.has(userId) || !userData.displayName) {
          console.log(`Skipping user ${userId}: already added or no displayName`);
          return;
        }
        
        // Filter out hidden users client-side
        if (userData.isHidden === true) {
          console.log(`Skipping user ${userId}: user is hidden`);
          return;
        }
        
        // Double-check onboarding status
        if (userData.onboardingComplete !== true) {
          console.log(`Skipping user ${userId}: onboarding not complete`);
          return;
        }
        
        // Create search result object
        const userResult = {
          id: userId,
          displayName: userData.displayName,
          firstName: userData.basicInfo?.firstName || '',
          lastName: userData.basicInfo?.lastName || '',
          currentRole: userData.basicInfo?.currentRole || '',
          companyName: userData.basicInfo?.companyName || '',
          profilePicture: userData.profilePicture || userData.basicInfo?.photoURL || userData.photoURL || '',
          lastActive: userData.lastLogin || userData.lastUpdated || userData.createdAt,
          searchScore: calculateSearchScore(userData, normalizedTerm)
        };
        
        console.log(`Added user ${userId} to results with score ${userResult.searchScore}`);
        userMap.set(userId, userResult);
      });
    });
    
    // If we have very few results, try a broader search as fallback
    if (userMap.size < 3) {
      try {
        console.log('Performing fallback search for better results');
        
        // Get more users and filter client-side
        const fallbackQuery = query(
          collection(db, 'user_profiles'),
          where('onboardingComplete', '==', true),
          where('isHidden', '!=', true),
          limit(50) // Get more docs to filter client-side
        );
        
        const fallbackSnapshot = await getDocs(fallbackQuery);
        
        fallbackSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          const userId = doc.id;
          
          // Skip if already added, if user data is incomplete, or if user is hidden
          if (userMap.has(userId) || !userData.displayName) {
            return;
          }
          
          // Filter out hidden users
          if (userData.isHidden === true) {
            return;
          }
          
          // Double-check onboarding status
          if (userData.onboardingComplete !== true) {
            return;
          }
          
          // Check if this user matches our search term (client-side filtering)
          const displayName = (userData.displayName || '').toLowerCase();
          const firstName = (userData.basicInfo?.firstName || '').toLowerCase();
          const lastName = (userData.basicInfo?.lastName || '').toLowerCase();
          const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
          
          if (displayName.includes(normalizedTerm) || 
              firstName.includes(normalizedTerm) || 
              lastName.includes(normalizedTerm) ||
              fullName.includes(normalizedTerm)) {
            
            const userResult = {
              id: userId,
              displayName: userData.displayName,
              firstName: userData.basicInfo?.firstName || '',
              lastName: userData.basicInfo?.lastName || '',
              currentRole: userData.basicInfo?.currentRole || '',
              companyName: userData.basicInfo?.companyName || '',
              profilePicture: userData.profilePicture || userData.basicInfo?.photoURL || userData.photoURL || '',
              lastActive: userData.lastLogin || userData.lastUpdated || userData.createdAt,
              searchScore: calculateSearchScore(userData, normalizedTerm)
            };
            
            console.log(`Fallback search found user ${userId}: ${userData.displayName}`);
            userMap.set(userId, userResult);
          }
        });
      } catch (fallbackError) {
        console.log('Fallback search failed:', fallbackError);
        // Continue with what we have
      }
    }
    
    // Convert to array and sort by relevance
    let users = Array.from(userMap.values());
    
    // Sort by search score (relevance) and then by name
    users.sort((a, b) => {
      if (a.searchScore !== b.searchScore) {
        return b.searchScore - a.searchScore; // Higher score first
      }
      return a.displayName.localeCompare(b.displayName);
    });
    
    // Limit results
    users = users.slice(0, maxResults);
    
    const result = {
      success: true,
      data: users
    };
    
    // Cache the result
    setCachedResult(cacheKey, result);
    
    // Update performance metrics
    const endTime = performance.now();
    const responseTime = endTime - startTime;
    searchMetrics.averageResponseTime = 
      (searchMetrics.averageResponseTime * (searchMetrics.totalSearches - 1) + responseTime) / searchMetrics.totalSearches;
    
    console.log(`Search completed for term "${searchTerm}":`, {
      totalQueries: searchQueries.length,
      totalResults: users.length,
      responseTime: `${responseTime.toFixed(2)}ms`,
      users: users.map(u => ({ id: u.id, displayName: u.displayName, score: u.searchScore }))
    });
    return result;
    
  } catch (error) {
    searchMetrics.errorCount++;
    console.error('Error searching users:', error);
    
    // Return cached results if available during error
    const cacheKey = `${searchTerm.trim().toLowerCase()}_${maxResults}`;
    const fallbackResult = getCachedResult(cacheKey, true); // Allow stale cache
    if (fallbackResult) {
      console.log('Returning stale cached results due to error');
      return {
        ...fallbackResult,
        isStale: true
      };
    }
    
    return {
      success: false,
      error: error.message || 'Failed to search users',
      data: []
    };
  }
};

/**
 * Calculate search relevance score
 * @param {Object} userData - User data from Firestore
 * @param {string} searchTerm - Normalized search term
 * @returns {number} - Relevance score (higher is better)
 */
const calculateSearchScore = (userData, searchTerm) => {
  let score = 0;
  const term = searchTerm.toLowerCase();
  
  const displayName = (userData.displayName || '').toLowerCase();
  const firstName = (userData.basicInfo?.firstName || '').toLowerCase();
  const lastName = (userData.basicInfo?.lastName || '').toLowerCase();
  const email = (userData.email || '').toLowerCase();
  
  // Exact matches get highest score
  if (displayName === term) score += 100;
  if (firstName === term) score += 90;
  if (lastName === term) score += 90;
  
  // Starts with matches
  if (displayName.startsWith(term)) score += 50;
  if (firstName.startsWith(term)) score += 40;
  if (lastName.startsWith(term)) score += 40;
  
  // Contains matches
  if (displayName.includes(term)) score += 20;
  if (firstName.includes(term)) score += 15;
  if (lastName.includes(term)) score += 15;
  if (email.includes(term)) score += 10;
  
  // Boost for recent activity
  const lastActive = userData.lastLogin || userData.lastUpdated;
  if (lastActive) {
    const daysSinceActive = (Date.now() - lastActive.toMillis()) / (1000 * 60 * 60 * 24);
    if (daysSinceActive < 7) score += 5;
    else if (daysSinceActive < 30) score += 2;
  }
  
  return score;
};

/**
 * Get cached search result with LRU management
 * @param {string} cacheKey - Cache key
 * @param {boolean} allowStale - Allow stale results during errors
 * @returns {Object|null} - Cached result or null
 */
const getCachedResult = (cacheKey, allowStale = false) => {
  const cached = searchCache.get(cacheKey);
  if (!cached) return null;
  
  const now = Date.now();
  const isExpired = now - cached.timestamp > CACHE_DURATION;
  
  if (isExpired && !allowStale) {
    searchCache.delete(cacheKey);
    return null;
  }
  
  // Move to end for LRU (most recently used)
  searchCache.delete(cacheKey);
  searchCache.set(cacheKey, cached);
  
  return cached.result;
};

/**
 * Set cached search result with LRU eviction
 * @param {string} cacheKey - Cache key
 * @param {Object} result - Result to cache
 */
const setCachedResult = (cacheKey, result) => {
  // Manage cache size with LRU eviction
  if (searchCache.size >= MAX_CACHE_SIZE) {
    // Remove oldest entry (first in Map)
    const firstKey = searchCache.keys().next().value;
    searchCache.delete(firstKey);
  }
  
  searchCache.set(cacheKey, {
    result,
    timestamp: Date.now()
  });
};

/**
 * Clear search cache
 */
export const clearSearchCache = () => {
  searchCache.clear();
  console.log('Search cache cleared');
};

/**
 * Get search performance metrics
 * @returns {Object} - Performance metrics
 */
export const getSearchMetrics = () => {
  return {
    ...searchMetrics,
    cacheHitRate: searchMetrics.totalSearches > 0 
      ? (searchMetrics.cacheHits / searchMetrics.totalSearches * 100).toFixed(2) + '%'
      : '0%',
    cacheSize: searchCache.size,
    maxCacheSize: MAX_CACHE_SIZE
  };
};

/**
 * Reset search metrics
 */
export const resetSearchMetrics = () => {
  searchMetrics = {
    totalSearches: 0,
    cacheHits: 0,
    averageResponseTime: 0,
    errorCount: 0
  };
  console.log('Search metrics reset');
};

/**
 * Get user by ID for mention validation
 * @param {string} userId - User ID to fetch
 * @returns {Promise<Object>} - User data or error
 */
export const getUserById = async (userId) => {
  try {
    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return {
        success: false,
        error: 'User not found'
      };
    }
    
    const userData = userSnap.data();
    
    // Check if user is available for mentioning
    if (!userData.onboardingComplete || userData.isHidden) {
      return {
        success: false,
        error: 'User not available for mentioning'
      };
    }
    
    return {
      success: true,
      data: {
        id: userId,
        displayName: userData.displayName,
        firstName: userData.basicInfo?.firstName || '',
        lastName: userData.basicInfo?.lastName || '',
        profilePicture: userData.profilePicture || userData.basicInfo?.photoURL || userData.photoURL || ''
      }
    };
    
  } catch (error) {
    console.error('Error fetching user by ID:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch user'
    };
  }
};